import { IItemForSelection } from '@/api/types/response.types'
import { IProfile } from '@/types/data-objects/profile'
import { AccountRoles } from '@/types/defined-types'
import { Store } from 'vuex'
import TypeAssistant from '@/helpers/type-assistant'

export default class Permissions {
  private store: Store<any>

  constructor(store: Store<any>) {
    this.store = store
  }

  public get isAdminOrOwner(): boolean {
    return this.isAdmin || this.isOwner
  }

  public get isNormalUser(): boolean {
    return this.loggedInUser.accountRole === 1
  }

  public get isOwner(): boolean {
    return this.loggedInUser.accountRole === 2
  }

  public get isAdmin(): boolean {
    return this.loggedInUser.accountRole === 3
  }

  public get isGuest(): boolean {
    return this.loggedInUser.accountRole === 4
  }

  public get isNormalUserOrGuest(): boolean {
    return this.isNormalUser || this.isGuest
  }

  public get isNotGuest(): boolean {
    return !this.isGuest
  }

  public get isImpersonating(): boolean {
    return this.loggedInUser.isImpersonating
  }

  public get loggedInUser(): IProfile {
    return this.store.state.profile.data
  }

  public isSampleInternalProjectID(id: any): boolean {
    return this.loggedInUser.sampleInternalProjectIDsForAccount.includes(
      parseInt(id, 10),
    )
  }

  public get isAppSumoAccount(): boolean {
    return this.loggedInUser.profileMetrics.userData.isAppSumoAccount
  }

  public isLoggedInUser(id: number): boolean {
    return this.loggedInUser.id === id
  }

  public get hasAdditionalAccounts(): boolean {
    return (
      this.loggedInUser.listAccounts &&
      this.loggedInUser.listAccounts.length > 1
    )
  }

  public canUpdateAccountRole(userID: number, accountRoleID: number): boolean {
    // You cannot edit your own role
    if (this.loggedInUser.id === userID) {
      return false
    }

    // Owners can update to any roles
    if (TypeAssistant.isOwnerRole(this.loggedInUser.accountRole)) {
      return true
    }

    // Admins can update to any roles but owner
    if (TypeAssistant.isAdminRole(this.loggedInUser.accountRole)) {
      return !TypeAssistant.isOwnerRole(accountRoleID)
    }

    // Else Normal and Client users can't update any roles
    // (nor will they have access to this screen)
    return false
  }

  public canDeleteAccountMembership(
    userID: number,
    accountRoleID: number,
  ): boolean {
    return this.canUpdateAccountRole(userID, accountRoleID)
  }

  public get availableRolesToAdmin(): IItemForSelection[] {
    // Normal and Guest users can't update any roles (nor will they have access to this screen)
    if (!this.isAdminOrOwner) {
      return []
    }

    // Owners can update to any roles
    if (this.isOwner) {
      return AccountRoles
    }

    // Admins can update to any roles but owner
    if (this.isAdmin) {
      return AccountRoles.filter(a => a.id !== 2)
    }
  }

  /* Feature flags */
  public get customWorkflowsFeatureEnabled(): boolean {
    return this.loggedInUser.featureEnablement.hasCustomWorkflows
  }

  public get jobsFeatureEnabled(): boolean {
    return this.loggedInUser.featureEnablement.hasJobs
  }

  public get ticketPortalFeatureEnabled(): boolean {
    return this.loggedInUser.featureEnablement.hasTicketPortal
  }

  /* Capabilities */
  public get isTieredPricingPlan(): boolean {
    return this.loggedInUser.featureEnablement.isTieredPricingPlan
  }
}
